import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    linkedSignal,
    signal,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { AttendeeComponent } from "./se.attendee.component";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { FormsModule } from "@angular/forms";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { attendanceDisplayName, attendanceState, attendanceStateName, AttendanceStateName } from "./attendance.service";
import { S25Util } from "../../../util/s25-util";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { SeAttendanceStore } from "./se.attendance.store";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { CardReaderService } from "../../../services/card.reader.service";
import { filter, map } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { Bind } from "../../../decorators/bind.decorator";
import { SpreadSheetService } from "../../s25-spreadsheet/spreadsheet.service";
import { SEEventDetailsStore } from "../event/se.event.details.store";
import { CardService } from "../../../services/card.service";
import { NotificationService } from "../../../services/notification.service";
import { IdCardsStore } from "../id-cards/id.cards.store";
import { ModalService } from "../../modal/modal.service";
import { S25Const } from "../../../util/s25-const";
import { S25EmailComponent } from "../../s25-email/s25.email.component";
import { ContactRole } from "../../../services/organization.service";
import { Item } from "../../../pojo/Item";
import { S25SidebarComponent } from "../../s25-sidebar/s25.sidebar.component";
import { SidebarStore } from "../../s25-sidebar/s25.sidebar.store";

type StateDropdownItem = DropDownItem<AttendanceStateName | "IN PROGRESS">;

@Component({
    standalone: true,
    selector: "s25-ng-manage-attendees",
    template: `
        @if (!readonly()) {
            <div class="options-group check-in">
                <span class="options-group--label">
                    {{ mode() === "checkin" ? "Check in" : "Add attendee" }}
                </span>
                <div class="options-group--options">
                    <s25-contact-dropdown
                        class="manage-attendees--contact-dropdown"
                        [searchEnabled]="true"
                        [noSelection]="mode() === 'checkin' ? 'Search contact to check in' : 'Search contact to add'"
                        [ariaLabel]="mode() === 'checkin' ? 'Search contact to check in' : 'Search contact to add'"
                        (valueChange)="addAttendee($event)"
                    />
                    @if (mode() === "checkin" && cardStore.hasConfigs()) {
                        <s25-ng-checkbox [(value)]="enableCardReader">Enable Card Reader</s25-ng-checkbox>
                    }
                </div>
            </div>
        }
        <div class="options-group-group">
            <div class="options-group">
                <span class="options-group--label">Filters</span>

                <div class="options-group--options">
                    <input
                        class="filter--keyword c-input"
                        type="text"
                        placeholder="Search existing attendees"
                        [(ngModel)]="filter.keyword"
                    />

                    @if (!!roleOptions()?.length) {
                        <s25-ng-generic-multiselect-dropdown
                            class="filter--roles"
                            [items]="roleOptions()"
                            [(value)]="filter.roles"
                            (valueChange)="copyArrayTo($any($event), filter.roles)"
                            [placeholder]="'Roles'"
                        />
                    }

                    @if (occurrences()?.length > 1) {
                        <s25-ng-generic-multiselect-dropdown
                            class="filter--occurrences"
                            [items]="occurrenceOptions()"
                            [(value)]="filter.occurrences"
                            (valueChange)="copyArrayTo($any($event), filter.occurrences)"
                            [placeholder]="'Occurrences'"
                        />
                    }

                    <s25-ng-generic-multiselect-dropdown
                        class="filter--state"
                        [items]="stateOptions"
                        [(value)]="filter.states"
                        (valueChange)="copyArrayTo($any($event), filter.states)"
                        [placeholder]="'Status'"
                    />
                </div>
            </div>

            <div class="options-group right">
                <span class="options-group--label">Actions</span>

                <div class="options-group--options">
                    <s25-ng-button [type]="'link'" [onClick]="export">Export</s25-ng-button>
                    @if (mode() !== "checkin") {
                        <s25-ng-button [type]="'outline'" (clicked)="openEmailSidebar()">
                            Email Attendees
                        </s25-ng-button>
                    }
                </div>
            </div>
        </div>

        <div class="attendees">
            @for (attendee of filteredAttendees(); track attendee.contactId) {
                <s25-ng-attendee [attendee]="attendee" [totalOccurrences]="occurrences()!.length" [mode]="mode()" />
            }
        </div>

        @if (mode() !== "checkin") {
            <s25-ng-sidebar title="Email Attendees" [mode]="'overlay'" [width]="600">
                <p class="sidebar-recipients">
                    Selected attendees of
                    <strong>{{ eventStore.name() }}</strong>
                </p>
                @if (sidebarStore.isOpen()) {
                    <s25-ng-email
                        [contextId]="eventStore.eventId() ?? 0"
                        [itemTypeId]="eventItemTypeId"
                        [orgMembers]="allEmailRecipients()"
                        [initialToRecipients]="emailRecipients()"
                        [memberLabel]="'Attendees'"
                        [showFormAssignment]="true"
                        (closed)="sidebarStore.close()"
                    />
                }
            </s25-ng-sidebar>
        }
    `,
    styles: `
        .options-group-group {
            display: flex;
            padding-block: 0.5rem;
            gap: 0.75rem;
            flex-wrap: wrap;
            align-items: stretch;
        }

        .options-group {
            padding: 0.5rem;
            border: 1px solid var(--color--border);
            border-radius: 0.375rem;
            background: var(--color--surface-bg);
            flex-grow: 1;
            display: grid;
            gap: 0.5rem;
        }

        .options-group.check-in {
            border-color: var(--color--link-text);
            background: var(--color--surface-2-bg);
            width: fit-content;
        }

        .options-group--label {
            flex-basis: 100%;
            font-size: 0.75rem;
            font-weight: 600;
            line-height: 1;
            color: var(--color--text-muted);
            text-transform: uppercase;
            letter-spacing: 0.03em;
        }

        .options-group--options {
            display: flex;
            gap: 1rem;
            align-items: center;
            flex-wrap: wrap;
        }

        .options-group.right .options-group--options {
            justify-content: flex-end;
        }

        /*
         * Override the default width of the contact dropdown to make it wider
         */
        :host ::ng-deep .manage-attendees--contact-dropdown .ui-select-dropdown {
            --default-width: calc(100% - 5px);
            --to-add: 10em;
            width: calc(var(--default-width) + var(--to-add)) !important;
            translate: calc(0px - var(--to-add) / 2) 0;
        }

        .filter--state,
        .filter--occurrences,
        .filter--roles {
            min-width: 13em;
        }

        .attendees {
            display: grid;
            row-gap: 1rem;
        }

        .sidebar-recipients {
            margin: 0 0 1rem;
            font-size: 13px;
            color: var(--color--text-muted);
            line-height: 1.5;
        }

        @media (max-width: 768px) {
            .options-group-group {
                flex-direction: column;
            }

            .options-group.check-in {
                width: 100%;
            }

            .options-group.check-in .options-group--options {
                flex-wrap: nowrap;
            }

            .options-group--options {
                gap: 0.5rem;
            }

            .filter--keyword,
            .manage-attendees--contact-dropdown {
                width: 100%;
            }

            .filter--roles,
            .filter--occurrences,
            .filter--state {
                flex: 1 1 100%;
                min-width: 0;
            }

            :host ::ng-deep .manage-attendees--contact-dropdown .ui-select-dropdown {
                --to-add: 0em;
                translate: 0 0;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25TableModule,
        AttendeeComponent,
        S25DropdownPaginatedModule,
        FormsModule,
        S25CheckboxModule,
        S25ButtonComponent,
        S25EmailComponent,
        S25SidebarComponent,
    ],
    providers: [CardReaderService, IdCardsStore, SidebarStore],
})
export class ManageAttendanceComponent {
    readonly occurrences = input.required<{ reservationId: number; date: string }[]>();
    readonly mode = input<"standard" | "checkin">("standard");

    readonly eventItemTypeId = Item.Ids.Event;
    readonly elementRef = inject(ElementRef);
    readonly eventStore = inject(SEEventDetailsStore);
    readonly orgSecStore = inject(SeOrgSecurityStore);
    readonly attendanceStore = inject(SeAttendanceStore);
    readonly cardStore = inject(IdCardsStore);
    readonly cardReader = inject(CardReaderService);
    readonly cardService = inject(CardService);
    readonly sidebarStore = inject(SidebarStore);
    readonly modalService = ModalService;
    private static readonly NOTIFICATION_DURATION = 10 * S25Const.ms.sec;

    readonly readonly = computed(() => !this.orgSecStore.canManageAttendees());
    readonly attendeesByInternalId = computed(() => {
        const attendees = this.attendanceStore.attendees() ?? [];
        return new Map(attendees.map((a) => [String(a.contactInternalId).toUpperCase(), a]));
    });
    readonly enableCardReader = signal(false);
    readonly cardSwipe$ = this.cardReader.byField$("internalId").pipe(
        filter(() => this.enableCardReader() && this.elementRef.nativeElement.checkVisibility()),
        takeUntilDestroyed(),
        map((ids) => ids[0]),
    );

    readonly stateOptions: StateDropdownItem[] = [
        { itemId: "INTERESTED", itemName: "Interested" },
        { itemId: "IN PROGRESS", itemName: "Pending" },
        { itemId: "REGISTERED", itemName: "Registered" },
        { itemId: "CHECKED_IN", itemName: "Checked In" },
        { itemId: "NO_SHOW", itemName: "No Show" },
    ];
    readonly occurrenceOptions = computed<DropDownItem<number>[]>(() => {
        return this.occurrences()?.map((occ) => ({ itemId: occ.reservationId, itemName: occ.date })) ?? [];
    });
    readonly attendees = computed(() => {
        // Map attendees to only show subset of occurrences that were passed in
        const occs = new Set(this.occurrences()?.map((occ) => occ.reservationId));
        return this.attendanceStore
            .attendees()
            ?.map((attendee) => {
                return {
                    ...attendee,
                    reservations: attendee.reservations.filter((r) => occs.has(r.reservationId)),
                };
            })
            .filter((attendee) => !!attendee.reservations.length);
    });
    readonly roleOptions = computed<DropDownItem<number>[]>(() => {
        const roles = this.attendees()?.flatMap((att) => att.orgRoles) ?? [];
        return S25Util.array
            .unique(roles, (role) => role.roleId)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((role) => ({ itemId: role.roleId, itemName: role.name }));
    });

    readonly filter = {
        keyword: signal<string>(""),
        states: linkedSignal<StateDropdownItem[]>(() => []),
        occurrences: signal<DropDownItem<number>[]>([]),
        roles: signal<DropDownItem<number>[]>([]),
    };
    readonly filteredAttendees = computed(() => {
        const keyword = this.filter.keyword().toLowerCase();
        const roles = new Set(this.filter.roles().map((role) => role.itemId));
        const occs = new Set(this.filter.occurrences().map((occ) => occ.itemId));
        const states = new Set(this.filter.states().map((state) => state.itemId));
        return (this.attendees() ?? []).filter((attendee) => {
            if (keyword) {
                const data = [attendee.contactName, attendee.contactEmail];
                const match = data.some((d) => d?.toLowerCase().includes(keyword));
                if (!match) return false;
            }
            if (!!roles.size) {
                const match = attendee.orgRoles.some((role) => roles.has(role.roleId));
                if (!match) return false;
            }
            if (!!occs.size) {
                const match = attendee.reservations.some((r) => occs.has(r.reservationId));
                if (!match) return false;
            }
            if (!!states.size) {
                const match = attendee.reservations.some((r) => {
                    if (!!occs.size && !occs.has(r.reservationId)) return false;
                    return states.has(attendanceStateName[r.attendeeState]);
                });
                if (!match) return false;
            }
            return true;
        });
    });

    readonly allEmailRecipients = computed<ContactRole[]>(() =>
        (this.attendees() ?? [])
            .filter((a) => !!a.contactEmail)
            .map((a) => ({
                itemName: a.contactName,
                itemId: a.contactId,
                itemEmail: a.contactEmail,
                itemTypeId: Item.Ids.Contact,
                isPrimary: false,
                role: { itemId: 0, itemName: "" },
            })),
    );

    readonly emailRecipients = computed<ContactRole[]>(() =>
        this.filteredAttendees()
            .filter((a) => !!a.contactEmail)
            .map((a) => ({
                itemName: a.contactName,
                itemId: a.contactId,
                itemEmail: a.contactEmail,
                itemTypeId: Item.Ids.Contact,
                isPrimary: false,
                role: { itemId: 0, itemName: "" },
            })),
    );

    constructor() {
        this.cardSwipe$.subscribe((id) => this.onCardSwipe(id));
    }

    openEmailSidebar() {
        if (!this.sidebarStore.isOpen()) this.sidebarStore.toggle();
    }

    copyArrayTo(arr: DropDownItem[], target: WritableSignal<DropDownItem[]>) {
        target.set([...arr]);
    }

    async addAttendee(contact: DropDownItem) {
        const contactId = Number(contact.itemId);

        if (this.mode() === "checkin") {
            const occurrences = this.occurrences().map((occ) => occ.reservationId);
            await this.attendanceStore.setAttendance("CHECKED_IN", { occurrences, contactId });
        } else {
            // Only add contact for occurrences that they are not already interested in
            const occIds = this.occurrences()?.map((occ) => occ.reservationId!) || [];
            const occsWithAttendance = new Set(
                this.attendanceStore.getAttendanceForContact(contactId)?.reservations.map((r) => r.reservationId) ?? [],
            );
            const occsToAdd = occIds.filter((occ) => !occsWithAttendance.has(occ));
            if (!occsToAdd.length) return;

            await this.attendanceStore.setAttendance("INTERESTED", { occurrences: occsToAdd, contactId: contactId });
        }

        NotificationService.post(
            `${this.mode() === "checkin" ? "Checked In" : "Added"} ${contact.itemName}`,
            ManageAttendanceComponent.NOTIFICATION_DURATION,
        );
    }

    async onCardSwipe(internalId: string) {
        const occurrences = this.occurrences().map((occ) => occ.reservationId);
        const occIds = new Set(occurrences);

        const attendee = this.attendeesByInternalId().get(internalId);
        // Check if already checked in
        if (attendee) {
            if (attendee.reservations.some((r) => occIds.has(r.reservationId) && r.checkedIn)) {
                NotificationService.post(`Already checked in`, ManageAttendanceComponent.NOTIFICATION_DURATION);
                return;
            }
        }

        let contactId = attendee?.contactId;
        let contactName = attendee?.contactName;
        if (!attendee) {
            const contact = await this.cardService.getContactFromInternalId(internalId);
            contactId = contact?.contId;
            contactName = contact?.fullName;
        }
        if (!contactId) {
            NotificationService.post(`No contact found`, ManageAttendanceComponent.NOTIFICATION_DURATION);
        }

        const registered = attendee?.reservations.some(
            (r) => occIds.has(r.reservationId) && r.attendeeState === attendanceState.REGISTERED,
        );
        if (!registered) {
            const ok = await this.modalService.dialog({
                type: "Yes No",
                primary: "Yes",
                title: "Confirm Check-In",
                message: `<strong>${contactName}</strong> is not registered.<br><br> Check in anyway?`,
            });
            if (!ok) return;
        }

        const [ok, err] = await S25Util.Maybe(
            this.attendanceStore.setAttendance("CHECKED_IN", { occurrences, contactId }),
        );
        if (err) {
            NotificationService.post(
                `Error checking in ${contactName}: ${err}`,
                ManageAttendanceComponent.NOTIFICATION_DURATION,
            );
        } else {
            NotificationService.post(`Checked in ${contactName}`, ManageAttendanceComponent.NOTIFICATION_DURATION);
        }
    }

    @Bind
    async export() {
        const attendances = this.filteredAttendees().flatMap((attendee) =>
            attendee.reservations.map((occurrence) => ({ attendee, occurrence })),
        );
        const byOccurrence = S25Util.array.groupBy(attendances, (a) => a.occurrence.reservationId);
        const dates = Object.fromEntries(
            (this.eventStore.occurrences() ?? []).map((occ) => [occ.reservationId, occ.startDate]),
        );
        const sheets = Object.entries(byOccurrence).map(([occId, attendees]) => ({
            name: dates[Number(occId)]!,
            data: (attendees ?? []).map((a) => ({
                Name: a.attendee.contactName,
                Email: a.attendee.contactEmail,
                Phone: a.attendee.contactPhone,
                Roles: a.attendee.orgRoles.map((r) => r.name).join(", "),
                Status: attendanceDisplayName[a.occurrence.attendeeState],
            })),
        }));
        await SpreadSheetService.downloadExcelMulti(sheets, `${this.eventStore.name()} Attendees.xlsx`);
    }
}
